import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/getImageUrl';
import StudentNotificationBell from './StudentNotificationBell';

const MahasiswaNav = () => {
  const location = useLocation();
  const { authUser, setAuthUser, signOut, refreshUser } = useAuth();
  const isActiveProgram = authUser?.activeSession?.statusCategory === 'ACTIVE_PROGRAM';
  const isBumdesProgram = authUser?.activeSession?.programCode === 'BUMDES' && isActiveProgram;

  const handleChangeBatch = async (batchId) => {
    try {
      await api.switchBatchContext({ batchId });
      await refreshUser();
    } catch (error) {
      console.error('Failed to switch batch:', error);
    }
  };

  return (
    <div className='d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 gap-lg-0 py-2 py-lg-0'>
      {/* 👤 User Menu */}
      <div className='dropdown'>
        <a
          href='#/'
          className='d-flex d-lg-inline-flex align-items-center justify-content-between justify-content-lg-start link-dark text-decoration-none dropdown-toggle px-3 py-2 rounded-pill bg-white border shadow-xs transition-all hover-scale'
          data-bs-toggle='dropdown'
          aria-expanded='false'
          style={{ border: '1px solid #e2e8f0' }}
        >
          <span className='d-flex align-items-center text-truncate'>
            {authUser?.imageUrl ? (
              <img
                src={getImageUrl(authUser.imageUrl)}
                alt=''
                className='rounded-circle me-2 border flex-shrink-0'
                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
              />
            ) : (
              <div
                className='p-1 bg-primary bg-opacity-10 text-primary rounded-circle me-2 d-flex align-items-center justify-content-center flex-shrink-0'
                style={{ width: '32px', height: '32px' }}
              >
                <i className='bi bi-person-fill small'></i>
              </div>
            )}
            <span className='text-start me-2 text-truncate'>
              <span
                className='d-block fw-bold text-dark mb-0 leading-tight text-truncate'
                style={{ fontSize: '0.875rem' }}
              >
                {authUser?.fullName}
              </span>
              <span
                className='d-block text-muted small'
                style={{ fontSize: '0.75rem' }}
              >
                {authUser?.activeSession?.roleName || 'Mahasiswa'}
              </span>
            </span>
          </span>
        </a>

        <ul
          className='dropdown-menu dropdown-menu-lg-end shadow-lg border-0 rounded-4 p-2 mt-2 mt-lg-3'
          style={{
            minWidth: '260px',
            maxWidth: 'calc(100vw - 2rem)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          {/* User Header Info */}
          <li className='px-3 py-2 mb-2'>
            <h6 className='fw-bold text-dark mb-0'>{authUser?.fullName}</h6>
            <small className='text-muted'>{authUser?.email}</small>
          </li>
          <li>
            <hr className='dropdown-divider opacity-50' />
          </li>

          {/* Domain: Program Utama */}
          <li className='dropdown-header small text-uppercase text-muted fw-bold pt-1 pb-1'>
            Program Utama
          </li>
          <li>
            <Link
              className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname === '/app' ? 'active' : ''}`}
              to='/app'
            >
              <i
                className={`bi bi-grid-fill ${location.pathname === '/app' ? 'text-white' : 'text-primary opacity-75'}`}
              ></i>
              <span>Dashboard</span>
            </Link>
          </li>

          <li>
            <Link
              className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/attendances') ? 'active' : ''}`}
              to='/app/attendances'
            >
              <i
                className={`bi bi-clock-history ${location.pathname.startsWith('/app/attendances') ? 'text-white' : 'text-primary opacity-75'}`}
              ></i>
              <span>Kehadiran</span>
            </Link>
          </li>

          <li>
            <Link
              className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/publication') ? 'active' : ''}`}
              to='/app/publication'
            >
              <i
                className={`bi bi-journal-text ${location.pathname.startsWith('/app/publication') ? 'text-white' : 'text-primary opacity-75'}`}
              ></i>
              <span>Publikasi</span>
            </Link>
          </li>

          <li>
            <Link
              className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/capacity-building') ? 'active' : ''}`}
              to='/app/capacity-building'
            >
              <i
                className={`bi bi-star-fill ${location.pathname.startsWith('/app/capacity-building') ? 'text-white' : 'text-primary opacity-75'}`}
              ></i>
              <span>Capacity Building</span>
            </Link>
          </li>

          {isActiveProgram && (
            <>
              <li>
                <hr className='dropdown-divider opacity-50' />
              </li>

              {/* Domain: Penilaian & Sertifikat */}
              <li className='dropdown-header small text-uppercase text-muted fw-bold pt-1 pb-1'>
                Penilaian &amp; Sertifikat
              </li>
              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/evaluation/pat') ? 'active' : ''}`}
                  to='/app/evaluation/pat'
                >
                  <i
                    className={`bi bi-clipboard-check-fill ${location.pathname.startsWith('/app/evaluation/pat') ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <div className='d-flex justify-content-between align-items-center w-100'>
                    <span>Penilaian (PAT)</span>
                    <span
                      className={`badge bg-success ${location.pathname.startsWith('/app/evaluation/pat') ? 'bg-opacity-25' : 'bg-opacity-10'} text-success rounded-pill x-small`}
                    >
                      New
                    </span>
                  </div>
                </Link>
              </li>

              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/evaluation/final') ? 'active' : ''}`}
                  to='/app/evaluation/final'
                >
                  <i
                    className={`bi bi-file-earmark-check-fill ${location.pathname.startsWith('/app/evaluation/final') ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>Penilaian (FINAL)</span>
                </Link>
              </li>

              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/evaluation/certificate') ? 'active' : ''}`}
                  to='/app/evaluation/certificate'
                >
                  <i
                    className={`bi bi-award-fill ${location.pathname.startsWith('/app/evaluation/certificate') ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>Cetak Sertifikat</span>
                </Link>
              </li>
            </>
          )}

          {isBumdesProgram && (
            <>
              <li>
                <hr className='dropdown-divider opacity-50' />
              </li>
              <li className='dropdown-header small text-uppercase text-muted fw-bold pt-1 pb-1'>
                BUM Desa
              </li>
              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname === '/app/bumdes/survey-veci' ? 'active' : ''}`}
                  to='/app/bumdes/survey-veci'
                >
                  <i
                    className={`bi bi-grid-1x2-fill ${location.pathname === '/app/bumdes/survey-veci' ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>BUMDesa VECI</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname === '/app/bumdes/survey-leadership' ? 'active' : ''}`}
                  to='/app/bumdes/survey-leadership'
                >
                  <i
                    className={`bi bi-speedometer2 ${location.pathname === '/app/bumdes/survey-leadership' ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>Survey Leadership</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/bumdes/bmc') ? 'active' : ''}`}
                  to='/app/bumdes/bmc'
                >
                  <i
                    className={`bi bi-grid-3x3-gap-fill ${location.pathname.startsWith('/app/bumdes/bmc') ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>BUMDesa BMC</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname.startsWith('/app/poc/red-cloud') ? 'active' : ''}`}
                  to='/app/poc/red-cloud'
                >
                  <i
                    className={`bi bi-clouds-fill ${location.pathname.startsWith('/app/poc/red-cloud') ? 'text-white' : 'text-primary opacity-75'}`}
                  ></i>
                  <span>Red Cloud</span>
                </Link>
              </li>
            </>
          )}

          <li>
            <hr className='dropdown-divider opacity-50' />
          </li>

          {/* Domain: Akun */}
          <li className='dropdown-header small text-uppercase text-muted fw-bold pt-1 pb-1'>
            Akun
          </li>
          <li>
            <Link
              className={`dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${location.pathname === '/app/profile' ? 'active' : ''}`}
              to='/app/profile'
            >
              <i
                className={`bi bi-person-circle ${location.pathname === '/app/profile' ? 'text-white' : 'text-primary opacity-75'}`}
              ></i>
              <span>Profil Saya</span>
            </Link>
          </li>
          <li>
            <Link
              className='dropdown-item d-flex align-items-center gap-2 py-2 rounded-3'
              to='/app/profile/update/identitas'
            >
              <i className='bi bi-pencil-square text-primary opacity-75'></i>
              <span>Ubah Profil</span>
            </Link>
          </li>

          <li>
            <hr className='dropdown-divider opacity-50' />
          </li>

          {/* Logout */}
          <li>
            <a
              onClick={(e) => {
                e.preventDefault();
                signOut();
              }}
              className='dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 text-danger'
              href='#/'
            >
              <i className='bi bi-box-arrow-right'></i>
              <span className='fw-bold'>Sign out</span>
            </a>
          </li>
        </ul>
      </div>

      {/* Divider */}
      <div className='vr mx-3 d-none d-lg-block'></div>

      {/* 🧩 Batch Selector */}
      <div className='dropdown text-lg-end'>
        <a
          href='#/'
          className='d-flex d-lg-inline-flex align-items-center justify-content-between justify-content-lg-start link-dark text-decoration-none dropdown-toggle px-3 py-2 rounded-pill bg-white border shadow-xs transition-all hover-scale'
          data-bs-toggle='dropdown'
          aria-expanded='false'
          style={{ border: '1px solid #e2e8f0' }}
        >
          <span className='d-flex align-items-center text-truncate'>
            <div
              className='p-1 bg-primary bg-opacity-10 text-primary rounded-circle me-2 d-flex align-items-center justify-content-center flex-shrink-0'
              style={{ width: '32px', height: '32px' }}
            >
              <i className='bi bi-arrow-left-right small'></i>
            </div>
            <span className='text-start me-2 text-truncate'>
              <span
                className='d-block fw-bold text-dark mb-0 leading-tight text-truncate'
                style={{ fontSize: '0.875rem' }}
              >
                {authUser?.activeSession?.programName || '-'}
              </span>
              <span
                className='d-block text-muted small'
                style={{ fontSize: '0.75rem' }}
              >
                Batch {authUser?.activeSession?.batchName || '-'}
              </span>
            </span>
          </span>
        </a>

        <ul
          className='dropdown-menu dropdown-menu-lg-end shadow-lg border-0 rounded-4 p-2 mt-2 mt-lg-3'
          style={{
            minWidth: '320px',
            maxWidth: 'calc(100vw - 2rem)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <li className='px-3 py-2 mb-1'>
            <h6 className='fw-bold text-dark mb-1'>Ganti Program</h6>
            <small className='text-muted'>Pilih program aktif Anda</small>
          </li>

          <div
            className='dropdown-scroll'
            style={{ maxHeight: '350px', overflowY: 'auto' }}
          >
            {authUser?.availableSessions?.map((program) => {
              const isActive =
                authUser?.activeSession?.batchId === program.batchId;

              return (
                <li
                  key={program.batchId}
                  className='mb-1'
                >
                  <div
                    role='button'
                    className={`dropdown-item d-flex align-items-center gap-3 p-3 rounded-3 transition-all cursor-pointer ${
                      isActive
                        ? 'bg-primary bg-opacity-10 border border-primary border-opacity-10'
                        : 'hover-bg-light'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isActive) handleChangeBatch(program.batchId);
                    }}
                  >
                    <div
                      className={`p-2 rounded-circle flex-shrink-0 ${isActive ? 'bg-primary text-white' : 'bg-light text-secondary'}`}
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i
                        className={`bi ${isActive ? 'bi-check-lg' : 'bi-journal-bookmark'}`}
                      ></i>
                    </div>
                    <div className='flex-grow-1 min-w-0'>
                      <div
                        className={`fw-bold mb-0 text-truncate ${isActive ? 'text-primary' : 'text-dark'}`}
                        style={{ fontSize: '0.9rem' }}
                      >
                        {program.programName}
                      </div>
                      <div
                        className='text-muted small truncate-2-lines'
                        style={{ fontSize: '0.75rem' }}
                      >
                        Batch {program.batchName} &nbsp;•&nbsp;
                        {new Date(program.startDate).toLocaleDateString(
                          'id-ID',
                          { day: 'numeric', month: 'long', year: 'numeric' },
                        )}{' '}
                        -{' '}
                        {new Date(program.endDate).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </div>

          {(!authUser?.availableSessions ||
            authUser.availableSessions.length === 0) && (
            <div className='p-4 text-center'>
              <i className='bi bi-inboxes text-muted fs-2 mb-2 d-block'></i>
              <span className='text-muted small fst-italic'>
                Tidak ada program ditawarkan
              </span>
            </div>
          )}
        </ul>
      </div>

      {/* Divider */}
      <div className='vr mx-3 d-none d-lg-block'></div>
      <div className='d-flex align-items-center gap-2'>
        <StudentNotificationBell />
        <span className='d-lg-none small text-muted'>Notifikasi</span>
      </div>
    </div>
  );
};

export default MahasiswaNav;
